import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../Firebase/firebase';
import { collection, addDoc, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { City } from '../data/City';
import { StartupField } from '../data/StartupField';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaLightbulb, FaMapMarkerAlt, FaTag, FaRocket } from 'react-icons/fa';
import { initializeStartupAnalytics } from '../pages/StartUpPage/utils/analyticsService';

const StartupCard = ({ setShowCard, showCard }) => {
  const [startupName, setStartupName] = useState('');
  // const [startupDesc, setStartupDesc] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const navigate = useNavigate();

  const handleCreateStartup = async (e) => {
    e.preventDefault();
    // Validate word count
    const wordCount = bio.trim() === '' ? 0 : bio.trim().split(/\s+/).length;
    if (wordCount > 20) {
      Swal.fire({
        icon: 'error',
        title: 'Word Limit Exceeded',
        text: 'Please keep your bio to 20 words or less.',
      });
    return;
  }
    setIsCreating(true);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        Swal.fire({
          icon: 'error',
          title: 'No User Logged In',
          text: 'Please log in to create a startup.',
        });
        return;
      }

      const { uid, displayName } = user;

      // Create the startup document
      const startupRef = await addDoc(collection(db, 'startups'), {
        startupName,
        stage: "Ideation",
        bio,
        solution: description, // Save description as 'solution'
        location,
        category,
        createdBy: displayName || 'Anonymous',
        creatorUid: uid,
        createdAt: new Date(),
        logoUrl: '', // Initialize empty logo URL
        coverPhotoUrl: '', // Initialize empty cover photo URL
      });

      // Initialize analytics for the new startup
      await initializeStartupAnalytics(startupRef.id);

      // Add startup to user's followed startups
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        followedStartups: arrayUnion(startupRef.id),
      });

      // Show success message
      await Swal.fire({
        icon: 'success',
        title: 'Business Created',
        text: 'Your Business Profile has been successfully created!',
        timer: 2000,
        showConfirmButton: false,
      });

      // Navigate to the new startup page
      navigate(`/startup/${startupRef.id}`);
      setShowCard(false);
    } catch (error) {
      console.error('Error creating Business Profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while creating your Business Profile. Please try again later.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setShowCard(false);
  };

  return (
    <AnimatePresence>
      {showCard && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 20 }}
            className="bg-white dark:bg-dark-primary p-6 rounded-xl shadow-2xl relative z-20 w-[360px] mx-4"
          >
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center">
                <FaRocket className="text-hypes-green dark:text-dark-accent mr-2" />
                <h2 className="text-xl font-bold text-gray-800 dark:text-dark-text">Create Your Business Profile</h2>
              </div>
              <button
                onClick={handleCancel}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateStartup}>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1 flex items-center">
                  <FaLightbulb className="mr-2 text-hypes-green dark:text-dark-accent" />
                  Business Name
                </label>
                <motion.input
                  type="text"
                  value={startupName}
                  onChange={(e) => setStartupName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-hypes-green dark:focus:ring-dark-accent focus:border-transparent text-sm dark:bg-dark-secondary dark:text-dark-text"
                  placeholder="Enter business name"
                  required
                  whileFocus={{ scale: 1.01 }}
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1 flex items-center">
                  <FaLightbulb className="mr-2 text-hypes-green dark:text-dark-accent" />
                  Bio (max 20 words)
                </label>
                <motion.textarea
                  value={bio}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/);
                    if (words.length <= 20 || e.target.value === '') {
                      setBio(e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-hypes-green dark:focus:ring-dark-accent focus:border-transparent text-sm min-h-[80px] dark:bg-dark-secondary dark:text-dark-text"
                  placeholder="Briefly describe your business (20 words max)"
                  required
                  whileFocus={{ scale: 1.01 }}
                />
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Word count: {bio.trim() === '' ? 0 : bio.trim().split(/\s+/).length}/20
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1 flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-hypes-green dark:text-dark-accent" />
                  Location
                </label>
                <motion.select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-hypes-green dark:focus:ring-dark-accent focus:border-transparent text-sm dark:bg-dark-secondary dark:text-dark-text"
                  required
                  whileFocus={{ scale: 1.01 }}
                >
                  <option value="">Select location</option>
                  {City.map((loc, index) => (
                    <option key={index} value={loc}>{loc}</option>
                  ))}
                </motion.select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1 flex items-center">
                  <FaTag className="mr-2 text-hypes-green dark:text-dark-accent" />
                  Category
                </label>
                <motion.select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-hypes-green dark:focus:ring-dark-accent focus:border-transparent text-sm dark:bg-dark-secondary dark:text-dark-text"
                  required
                  whileFocus={{ scale: 1.01 }}
                >
                  <option value="">Select category</option>
                  {StartupField.map((cat, index) => (
                    <option key={index} value={cat}>{cat}</option>
                  ))}
                </motion.select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-1 flex items-center">
                  <FaLightbulb className="mr-2 text-hypes-green dark:text-dark-accent" />
                  Description
                </label>
                <motion.textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-hypes-green dark:focus:ring-dark-accent focus:border-transparent text-sm min-h-[80px] dark:bg-dark-secondary dark:text-dark-text"
                  placeholder="Describe your business in detail"
                  required
                  whileFocus={{ scale: 1.01 }}
                />
              </div>

              <div className="flex justify-between">
                <motion.button
                  type="submit"
                  className="bg-hypes-green dark:bg-dark-accent text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-hypes-green-dark dark:hover:bg-emerald-600 transition-all duration-200 flex items-center"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaRocket className="mr-2" />
                      Create Business Profile
                    </>
                  )}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isCreating}
                >
                  Cancel
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StartupCard;